import { Phone, Target, Pill, Activity, AlertTriangle, ClipboardList, FlaskConical, Calendar, Hospital } from 'lucide-react';
import { formatStatusSource } from '@/components/patient/modals/StatusChangeModal';
import { OpenAlertBadge, StatusBadge } from '@/components/shared/StatusBadge';
import { formatCancerDisplay, formatTreatmentDisplay } from '@/components/shared/OncologyContext';
import { useNavigate } from 'react-router-dom';

/**
 * En-tête du patient avec avatar, informations principales et boutons d'action
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */
export default function PatientDetailHeader({ 
  patient, 
  patientId,
  onCallPatient,
  actionButtons,
  showEvaluationBanner = false,
  userRole = "nurse", // "nurse" | "doctor"
  studyEnrollments = [],
}) {
  const navigate = useNavigate();

  return (
    <div className="dash-card">
      {/* Clinical Evaluation Alert Banner - Show when evaluation is needed */}
      {showEvaluationBanner && !patient.clinicalEvaluationComplete && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Évaluation clinique requise</span>
            <span className="text-amber-600 text-sm">- Le dossier sera activé après complétion</span>
          </div>
          <button
            onClick={() => navigate(userRole === "doctor" ? `/doctor/patients/${patientId}/evaluation` : `/nurse/patients/${patientId}/evaluation`)}
            data-testid="start-evaluation-btn"
            className="flex items-center gap-2 bg-amber-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-amber-700 transition-colors"
          >
            <ClipboardList className="w-4 h-4" />
            Compléter l'évaluation
          </button>
        </div>
      )}
      
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
            {patient.firstName?.[0]}{patient.lastName?.[0]}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl font-bold text-slate-800 font-display">
                {patient.firstName} {patient.lastName}
              </h1>
              <span className="inline-flex items-center gap-1.5" title="État clinique (dernier événement d'évaluation)">
                <StatusBadge status={patient.currentStatus || patient.status || "STABLE"} size="lg" testId={`patient-header-current-status-${patientId}`} />
                {patient.statusMeta?.source === "manual" && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-700 font-medium" title={formatStatusSource(patient.statusMeta)} data-testid={`patient-header-status-manual-${patientId}`}>
                    corrigé IDE
                  </span>
                )}
              </span>
              <OpenAlertBadge count={patient.openAlertsCount} level={patient.highestOpenAlertLevel} size="lg" testId={`patient-header-open-alerts-${patientId}`} />
              {patient.isActive === false && (
                <span
                  className="text-xs px-2 py-1 rounded-full font-medium bg-slate-800 text-white"
                  title={patient.deactivation?.reasonCode ? `Motif : ${patient.deactivation.reasonCode}` : undefined}
                  data-testid={`patient-header-inactive-${patientId}`}
                >
                  Suivi inactif
                </span>
              )}
              {patient.dossierStatus === "hospitalise" && (
                <span
                  className="text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-700 inline-flex items-center gap-1"
                  title={`Hospitalisation confirmée${patient.hospitalizedAt ? ` depuis le ${new Date(patient.hospitalizedAt).toLocaleDateString("fr-FR")}` : ""}`}
                  data-testid={`patient-header-hospitalized-${patientId}`}
                >
                  <Hospital className="w-3 h-3" />
                  Hospitalisé
                </span>
              )}
              {patient.dossierStatus && !["actif", "hospitalise"].includes(patient.dossierStatus) && (
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  patient.dossierStatus === "dossier_cree" ? "bg-slate-100 text-slate-600" :
                  patient.dossierStatus === "evaluation_requise" ? "bg-amber-100 text-amber-700" :
                  patient.dossierStatus === "suspendu" ? "bg-red-100 text-red-700" :
                  "bg-slate-100 text-slate-600"
                }`}>
                  {patient.dossierStatus === "dossier_cree" ? "Dossier créé" :
                   patient.dossierStatus === "evaluation_requise" ? "Évaluation requise" :
                   patient.dossierStatus === "suspendu" ? "Suspendu" :
                   patient.dossierStatus === "termine" ? "Terminé" :
                   patient.dossierStatus}
                </span>
              )}
              {(patient.treatmentStatus === "Suspendu" || patient.treatmentStatus === "Arrêté") && (
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium ${patient.treatmentStatus === "Suspendu" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}
                  title="Statut du traitement (plan de traitement)"
                  data-testid={`patient-header-treatment-status-${patientId}`}
                >
                  Traitement {patient.treatmentStatus.toLowerCase()}
                </span>
              )}
              {studyEnrollments.length > 0 && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-indigo-100 text-indigo-700 flex items-center gap-1" data-testid="study-badge">
                  <FlaskConical className="w-3 h-3" />
                  {studyEnrollments.length} etude{studyEnrollments.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <button 
                onClick={onCallPatient}
                data-testid="patient-header-phone-button"
                className="flex items-center gap-1 hover:text-primary transition-colors"
              >
                <Phone className="w-4 h-4" /> {patient.phone}
              </button>
              <span className="flex items-center gap-1"><Target className="w-4 h-4" /> {formatCancerDisplay(patient)}</span>
              <span className="flex items-center gap-1"><Pill className="w-4 h-4" /> {formatTreatmentDisplay(patient)}</span>
              {patient.therapeuticLine && (
                <span className="flex items-center gap-1"><Activity className="w-4 h-4" /> {patient.therapeuticLine}</span>
              )}
              {patient.treatmentStartDate && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Calendar className="w-4 h-4" /> J+{Math.floor((Date.now() - new Date(patient.treatmentStartDate).getTime()) / (1000 * 60 * 60 * 24))}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons - passed from parent */}
      {actionButtons && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
          {actionButtons}
        </div>
      )}
    </div>
  );
}
